/**
 * @file One View Bidders table row, memoized so a pick on another row
 * does not rebuild this subtree.
 */

import React from 'react';
import { ChevronDown, ChevronRight, Scan, CheckCircle2, ExternalLink, Package } from 'lucide-react';
import BidderPrintButton from './BidderPrintButton';
import BidderExpandedLots from './BidderExpandedLots';
import { decodeHtmlEntities } from '../utils/htmlDecode';

function BidderActionButtons({
  bidder,
  auctionGroupId,
  printFormat,
  onScanItems,
  onToggleMarkedPicked,
  onToggleLoadedOut,
  markedPickedPending,
  loadedOutPending,
}) {
  return (
    <>
      <BidderPrintButton
        bidderId={bidder.bidder_id}
        auctionGroupId={auctionGroupId}
        format={printFormat}
      />
      <button
        onClick={() => onScanItems(bidder.bidder_id)}
        className="btn btn-primary action-button"
      >
        <Scan size={14} />
        Scan
      </button>
      <button
        onClick={() => onToggleMarkedPicked(bidder)}
        className={`btn action-button ${bidder.is_marked_picked ? 'btn-danger' : 'btn-success'}`}
        style={{
          backgroundColor: bidder.is_marked_picked ? '#dc3545' : '#28a745',
          color: 'white',
          border: 'none',
          opacity: markedPickedPending ? 0.7 : 1,
        }}
        title={markedPickedPending
          ? 'Saved on this device — waiting to sync'
          : 'Mark bidder as picked (independent of loaded out)'}
      >
        <CheckCircle2 size={14} />
        {bidder.is_marked_picked ? 'Unpick' : 'Picked'}
      </button>
      <button
        onClick={() => onToggleLoadedOut(bidder)}
        className={`btn action-button ${bidder.is_picked ? 'btn-danger' : 'btn-success'}`}
        style={{
          backgroundColor: bidder.is_picked ? '#dc3545' : '#28a745',
          color: 'white',
          border: 'none',
          opacity: loadedOutPending ? 0.7 : 1,
        }}
        title={loadedOutPending
          ? 'Saved on this device — waiting to sync'
          : 'Mark items as loaded out'}
      >
        <Package size={14} />
        {bidder.is_picked ? 'Unload' : 'Loaded Out'}
      </button>
    </>
  );
}

function BidderRow({
  bidder,
  auctionGroupId,
  isMobile,
  isExpanded,
  printFormat,
  markedPickedPending,
  loadedOutPending,
  onToggleRow,
  onScanItems,
  onToggleMarkedPicked,
  onToggleLoadedOut,
  onOpenHistory,
}) {
  const rowClass = [
    bidder.has_open_invoice && bidder.is_picked
      ? 'bidder-open-loaded'
      : bidder.has_open_invoice
        ? 'bidder-open'
        : '',
    bidder.is_picked && !bidder.has_open_invoice ? 'bidder-loaded-out' : '',
  ].filter(Boolean).join(' ');

  return (
    <React.Fragment>
      <tr className={rowClass}>
        <td>
          {!isMobile && (
            <button
              onClick={() => onToggleRow(bidder.bidder_id)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '8px' }}
            >
              {isExpanded ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </button>
          )}
          {bidder.bidder_id}
        </td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {bidder.is_marked_picked && (
              <CheckCircle2
                size={20}
                color="#28a745"
                aria-label="Picked"
                title="Picked"
                style={{ flexShrink: 0 }}
              />
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenHistory(bidder);
              }}
              title="View bidder history"
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: '#007bff',
                cursor: 'pointer',
                textDecoration: 'underline',
                font: 'inherit',
                textAlign: 'left',
              }}
            >
              {decodeHtmlEntities(bidder.bidder_name) || bidder.bidder_id}
            </button>
          </div>
        </td>
        <td>
          {bidder.invoice_numbers ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {bidder.invoice_numbers.split(', ').map((invoiceNum, idx) => {
                const trimmedInvoice = invoiceNum.trim();
                if (!trimmedInvoice) return null;
                return (
                  <a
                    key={idx}
                    href={`https://www.equip-bid.com/dashboard/invoices/${trimmedInvoice}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      border: 'none',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#0056b3';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#007bff';
                    }}
                  >
                    {trimmedInvoice}
                    <ExternalLink size={14} />
                  </a>
                );
              })}
            </div>
          ) : (
            'N/A'
          )}
        </td>
        <td>{bidder.item_count}</td>
        <td>
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            backgroundColor: bidder.scan_status === 'Scanned' ? '#d4edda'
              : bidder.scan_status === 'Partial Scan' ? '#fff3cd' : '#f8d7da',
            color: bidder.scan_status === 'Scanned' ? '#155724'
              : bidder.scan_status === 'Partial Scan' ? '#856404' : '#721c24',
          }}
          >
            {bidder.scan_status || 'Not Scanned'}
          </span>
        </td>
        {!isMobile && (
          <td className="actions-column">
            <div className="action-buttons">
              <BidderActionButtons
                bidder={bidder}
                auctionGroupId={auctionGroupId}
                printFormat={printFormat}
                onScanItems={onScanItems}
                onToggleMarkedPicked={onToggleMarkedPicked}
                onToggleLoadedOut={onToggleLoadedOut}
                markedPickedPending={markedPickedPending}
                loadedOutPending={loadedOutPending}
              />
            </div>
          </td>
        )}
      </tr>
      {isMobile && (
        <tr className="mobile-action-buttons-row">
          <td colSpan="6">
            <div className="mobile-action-buttons-wrapper">
              <div className="mobile-action-buttons">
                <BidderActionButtons
                  bidder={bidder}
                  auctionGroupId={auctionGroupId}
                  printFormat={printFormat}
                  onScanItems={onScanItems}
                  onToggleMarkedPicked={onToggleMarkedPicked}
                  onToggleLoadedOut={onToggleLoadedOut}
                  markedPickedPending={markedPickedPending}
                  loadedOutPending={loadedOutPending}
                />
              </div>
            </div>
          </td>
        </tr>
      )}
      {!isMobile && isExpanded && (
        <tr className="lot-details-row">
          <td colSpan="6" className="lot-details-container">
            <BidderExpandedLots
              bidderId={bidder.bidder_id}
              auctionGroupId={auctionGroupId}
              enabled={isExpanded}
            />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

export default React.memo(BidderRow);
